import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Sparkles, Send, Trash2, AlertCircle } from 'lucide-react';
import { AddonsLibraryItem, createAddonsLibraryItem } from '../lib/addonsLibrary';
import { supabase } from '../lib/supabase';
import { useBusiness } from '../contexts/BusinessContext';
import TokenPurchaseModal from './TokenPurchaseModal';

interface AIAddonCreatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddonsCreated: (addons: AddonsLibraryItem[]) => void;
  businessId: string;
}

interface GeneratedAddon {
  name: string;
  description: string;
  price: number;
  duration_minutes: number;
}

interface AIMessage {
  role: 'user' | 'assistant';
  content: string;
}

export default function AIAddonCreatorModal({
  isOpen,
  onClose,
  onAddonsCreated,
  businessId
}: AIAddonCreatorModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [conversation, setConversation] = useState<AIMessage[]>([]);
  const [generatedAddons, setGeneratedAddons] = useState<GeneratedAddon[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTokenPurchaseModal, setShowTokenPurchaseModal] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setConversation([{
        role: 'assistant',
        content: "Hi! I'll help you create add-ons using AI. Tell me what add-ons you'd like to create. For example: 'Create add-ons for a car detailing package' or 'Create 5 protective coating add-ons'"
      }]);
    } else {
      setTimeout(() => {
        setIsAnimating(false);
        setConversation([]);
        setGeneratedAddons([]);
        setPrompt('');
        setError(null);
      }, 200);
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!prompt.trim() || isGenerating) return;

    const userMessage = prompt.trim();
    setPrompt('');
    setError(null);

    setConversation(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsGenerating(true);

    try {
      const { data, error: invokeError } = await supabase.functions.invoke('ai-create-addons', {
        body: {
          prompt: userMessage,
          conversationHistory: conversation,
          businessId: selectedBusiness?.id,
          businessName: selectedBusiness?.business_name
        }
      });

      if (data?.insufficientTokens) {
        setShowTokenPurchaseModal(true);
        setError('Insufficient tokens. Please purchase more to continue.');
        setIsGenerating(false);
        return;
      }

      if (invokeError) throw invokeError;

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to generate add-ons');
      }

      if (data.message) {
        setConversation(prev => [...prev, { role: 'assistant', content: data.message }]);
      }

      if (data.addons && data.addons.length > 0) {
        setGeneratedAddons(data.addons);
      }

    } catch (error: any) {
      console.error('Error generating add-ons:', error);
      setError(error.message || 'Failed to generate add-ons');
      setConversation(prev => [...prev, { 
        role: 'assistant', 
        content: `Sorry, I encountered an error: ${error.message || 'Please try again.'}` 
      }]);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCreateAddons = async () => {
    if (generatedAddons.length === 0) return;

    setIsCreating(true);
    setError(null);

    try {
      console.log('🚀 Creating add-ons:', generatedAddons);
      const createdAddons: AddonsLibraryItem[] = [];

      for (const addonData of generatedAddons) {
        console.log('Creating add-on:', addonData.name);
        const addon = await createAddonsLibraryItem({
          business_id: businessId,
          name: addonData.name,
          description: addonData.description,
          price: addonData.price,
          duration_minutes: addonData.duration_minutes
        });

        console.log('✅ Add-on created:', addon.name);
        createdAddons.push(addon);
      }

      console.log('✅ All add-ons created successfully:', createdAddons.length);
      
      // First close the AI modal
      onClose();
      
      // Then call the callback after a short delay to ensure parent refreshes
      setTimeout(() => {
        onAddonsCreated(createdAddons);
      }, 100);
    } catch (error: any) {
      console.error('❌ Error creating add-ons:', error);
      setError(error.message || 'Failed to create add-ons');
    } finally {
      setIsCreating(false);
    }
  };

  const updateAddon = (index: number, field: string, value: any) => {
    setGeneratedAddons(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeAddon = (index: number) => {
    setGeneratedAddons(prev => prev.filter((_, i) => i !== index));
  };

  if (!isOpen && !isAnimating) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const modalContent = (
    <div
      className="fixed inset-0 transition-opacity duration-200 bg-black/50"
      style={{
        zIndex: 100500,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      onClick={onClose}
    >
      <div
        className={`bg-[#1e1e1e] shadow-2xl flex flex-col overflow-hidden transform transition-all duration-200 ${
          isMobile
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[800px] lg:w-[900px] ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100510,
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          paddingLeft: 'env(safe-area-inset-left, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-6 relative flex items-center justify-between border-b border-blue-400/20">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">AI Add-on Creator</h2>
              <p className="text-sm text-blue-100 mt-0.5">
                Let Nexus AI help you create add-ons
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="h-8 w-8 flex items-center justify-center text-white/80 hover:text-white rounded-full hover:bg-white/10 active:bg-white/20 active:scale-95 transition-all duration-150"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col bg-[#1e1e1e]">
          {/* Conversation Area */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-[#2a2a2a]">
            {conversation.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    message.role === 'user'
                      ? 'bg-blue-500 text-white'
                      : 'bg-[#3a3a3a] border border-gray-700 text-gray-100'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                </div>
              </div>
            ))}

            {isGenerating && (
              <div className="flex justify-start">
                <div className="bg-[#3a3a3a] border border-gray-700 rounded-2xl px-4 py-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Generated Add-ons Preview */}
          {generatedAddons.length > 0 && (
            <div className="border-t border-gray-700 bg-[#1e1e1e] p-6 max-h-[40vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-100">Generated Add-ons ({generatedAddons.length})</h3>
                <button
                  onClick={handleCreateAddons}
                  disabled={isCreating}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 active:scale-95 transition-all duration-150 disabled:bg-gray-600 disabled:cursor-not-allowed"
                >
                  {isCreating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      <span className="text-sm font-semibold">Creating...</span>
                    </>
                  ) : (
                    <span className="text-sm font-semibold">Create All Add-ons</span>
                  )}
                </button>
              </div>

              <div className="space-y-3">
                {generatedAddons.map((addon, index) => (
                  <div key={index} className="bg-[#2a2a2a] border border-gray-700 rounded-xl p-4">
                    <div className="flex items-start justify-between mb-3">
                      <input
                        type="text"
                        value={addon.name}
                        onChange={(e) => updateAddon(index, 'name', e.target.value)}
                        className="flex-1 text-base font-bold text-gray-100 bg-[#3a3a3a] border border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Add-on Name"
                      />
                      <button
                        onClick={() => removeAddon(index)}
                        className="ml-3 p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    <input
                      type="text"
                      value={addon.description}
                      onChange={(e) => updateAddon(index, 'description', e.target.value)}
                      className="w-full text-sm text-gray-200 bg-[#3a3a3a] border border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3"
                      placeholder="Description"
                    />

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-400 mb-1">Price ($)</label>
                        <input
                          type="number"
                          value={addon.price}
                          onChange={(e) => updateAddon(index, 'price', parseFloat(e.target.value) || 0)}
                          className="w-full text-sm text-gray-100 bg-[#3a3a3a] border border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="0.00"
                          step="0.01"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-400 mb-1">Duration (mins)</label>
                        <input
                          type="number"
                          value={addon.duration_minutes}
                          onChange={(e) => updateAddon(index, 'duration_minutes', parseInt(e.target.value) || 0)}
                          className="w-full text-sm text-gray-100 bg-[#3a3a3a] border border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="0"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="border-t border-gray-700 bg-red-900/20 px-6 py-3">
              <div className="flex items-center text-red-400 text-sm">
                <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                {error}
              </div>
            </div>
          )}

          {/* Input Area */}
          <div className="border-t border-gray-700 bg-[#1e1e1e] p-4">
            <form onSubmit={handleSubmit} className="flex items-center space-x-3">
              <input
                ref={inputRef}
                type="text"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe the add-ons you want to create..."
                className="flex-1 px-4 py-3 bg-[#2a2a2a] border border-gray-700 text-gray-100 placeholder-gray-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                disabled={isGenerating}
              />
              <button
                type="submit"
                disabled={!prompt.trim() || isGenerating}
                className="p-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 active:scale-95 transition-all duration-150 disabled:bg-gray-700 disabled:cursor-not-allowed"
              >
                {isGenerating ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <Send className="h-5 w-5" />
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Token Purchase Modal */}
      {showTokenPurchaseModal && (
        <TokenPurchaseModal
          isOpen={showTokenPurchaseModal}
          onClose={() => setShowTokenPurchaseModal(false)}
          businessId={selectedBusiness?.id || ''}
        />
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
}

